import React, { FunctionComponent, ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { useHistory } from 'react-router-dom';
import { Button } from '@empiriecom/module-components/Button';
import { Section } from '@/src/components/Card';
import { RatingReview } from '@/src/interfaces/Rating';
import RatingsSummary from './RatingsSummary';
import RatingCard from './RatingCard';
import { RatingSummarySkeleton } from './RatingsSummary/RatingSummarySkeleton';
import { useRating } from '../ProductContext';

const RatingsWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-flow: ${({ isMobile }) => (isMobile ? 'row wrap;' : 'row nowrap;')};
  width: 100%;
  padding-bottom: 0.75rem;
  @media (min-width: 48em) {
    padding-bottom: 1.5rem;
  }
`;

const RatingsListWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%;' : '60%;')};
`;

const ShowAllRatingsButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1rem auto;
`;

const ShowAllRatingsButton = styled(Button)`
  font-size: 0.6875rem;
`;

/**
 * <p>This is the customer ratings component.</p>
 */
export const CustomerRatings: FunctionComponent = (): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const history = useHistory();

  const { error, isLoading, rating: response } = useRating();

  return (
    <>
      {error && null}
      {isLoading && <RatingSummarySkeleton />}
      {response && (
        <Section
          headline={<FormattedMessage id="ratings.headline" defaultMessage="Kundenbewertungen" />}
        >
          <RatingsWrapper data-testid="customer-ratings" isMobile={isMobile}>
            <RatingsSummary response={response} />
            {response.product.reviews && response.product.avgRating > 0 && (
              <RatingsListWrapper isMobile={isMobile}>
                {response.product.reviews.length > 0 &&
                  response.product.reviews.map((rating: RatingReview) => (
                    <RatingCard key={rating.id} rating={rating} />
                  ))}
                <ShowAllRatingsButtonWrapper>
                  <ShowAllRatingsButton
                    asRouterLink
                    uppercase={false}
                    to={{
                      ...history.location,
                      pathname: history.location.pathname.concat('/rating'),
                    }}
                    layout="default"
                    data-testid="show-all-ratings"
                  >
                    <FormattedMessage
                      id="ratings.show-all"
                      defaultMessage="Alle Bewertungen ({ratingCount}) anzeigen"
                      values={{ ratingCount: response.product.reviewCount }}
                    />
                  </ShowAllRatingsButton>
                </ShowAllRatingsButtonWrapper>
              </RatingsListWrapper>
            )}
          </RatingsWrapper>
        </Section>
      )}
    </>
  );
};

export default CustomerRatings;
